"use client";

import {
  createContext,
  startTransition,
  use,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useOptimistic,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useRouter } from "next/navigation";

import {
  clearCartAction,
  removeLineAction,
  setLineImageAction,
  setLineStatusAction,
  updateLineAction,
  upsertLineAction,
} from "./actions";
import { describeThrown } from "@/lib/api-failure";
import { newClientId } from "@/lib/client-id";
import { findLineByDedup } from "./dedup";
import {
  CART_HOLD_MINUTES,
  EMPTY_CART,
  type Cart,
  type CartLine,
  type CartLineDraft,
  type CartLineStatus,
} from "./types";

type OptimisticAction =
  | { type: "upsert"; line: CartLine }
  | { type: "update"; id: string; patch: Partial<CartLine> }
  | { type: "remove"; id: string }
  | { type: "clear" }
  | { type: "status"; id: string; status: CartLineStatus; message?: string }
  | { type: "image"; id: string; url: string };

function reducer(state: Cart, action: OptimisticAction): Cart {
  switch (action.type) {
    case "upsert": {
      // Match the server's dedup logic so the optimistic state never shows a
      // duplicate line that the server would have merged. Without this, the
      // user briefly sees two identical lines until revalidate snaps the
      // cart back to truth.
      const existing = findLineByDedup(state.lines, action.line);
      if (existing) {
        return {
          ...state,
          lines: state.lines.map((l) =>
            l.id === existing.id
              ? { ...l, ...action.line, id: existing.id }
              : l,
          ),
        };
      }
      return { ...state, lines: [...state.lines, action.line] };
    }
    case "update":
      return {
        ...state,
        lines: state.lines.map((l) =>
          l.id === action.id ? { ...l, ...action.patch } : l,
        ),
      };
    case "remove":
      return { ...state, lines: state.lines.filter((l) => l.id !== action.id) };
    case "clear":
      return { ...state, lines: [] };
    case "status":
      return {
        ...state,
        lines: state.lines.map((l) =>
          l.id === action.id
            ? { ...l, status: action.status, statusMessage: action.message }
            : l,
        ),
      };
    case "image":
      return {
        ...state,
        lines: state.lines.map((l) =>
          l.id === action.id && l.experienceImageUrl !== action.url
            ? { ...l, experienceImageUrl: action.url }
            : l,
        ),
      };
  }
}

interface CartContextValue {
  cart: Cart;
  upsertLine: (draft: CartLineDraft) => string;
  updateLine: (id: string, patch: Partial<CartLine>) => void;
  removeLine: (id: string) => Promise<boolean>;
  clearCart: () => void;
  setStatus: (id: string, status: CartLineStatus, message?: string) => void;
  setImageUrl: (id: string, url: string) => void;
  /** The last write that failed, for the page to state in place. `null` once
   *  dismissed or once a later write succeeds. */
  writeError: string | null;
  dismissWriteError: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

function nextHoldExpiry(): string {
  return new Date(Date.now() + CART_HOLD_MINUTES * 60_000).toISOString();
}

/**
 * The last server-confirmed cart any provider held. A client move between a
 * built page (`(public)`) and an ISR page (`sites`) mounts a new provider, and
 * `CartProviderLive` starts from this instead of an empty cart, so the badge
 * does not drop to zero while `/api/cart` answers (#923). Module memory: a full
 * page load starts empty, as the server render does.
 */
let lastKnownCart: Cart | null = null;

/**
 * CartProviderCore — the shared cart engine. Given an already-resolved
 * `base` Cart and an `onReconcile` callback, it owns the optimistic state,
 * the mutation helpers, the serialised write queue, and the context value.
 * Both seed wrappers below feed the same core; they differ only in where
 * `base` comes from (a server `cartPromise` vs a client `fetch('/api/cart')`)
 * and how `onReconcile` re-bases the cart after a write.
 *
 * Mutations are optimistic: they update the local UI synchronously, then fire
 * the matching Server Action that writes the cookie. After each queued write
 * `onReconcile()` re-seeds `base` from server-truth and `useOptimistic`
 * reconciles.
 */
function CartProviderCore({
  children,
  base,
  onReconcile,
}: {
  children: ReactNode;
  base: Cart;
  onReconcile: () => void;
}) {
  const [optimisticCart, dispatch] = useOptimistic(base, reducer);

  useEffect(() => {
    // EMPTY_CART is a placeholder until a seed answers, not a confirmed cart.
    if (base !== EMPTY_CART) lastKnownCart = base;
  }, [base]);

  // Per-instance write queue — serialises Server Action calls so each action
  // sees the cookie the previous one wrote. Stored in a ref so the queue
  // survives re-renders without leaking state across component instances or
  // test runs.
  const writeQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const enqueue = useCallback(<T,>(task: () => Promise<T>): Promise<T> => {
    const next = writeQueueRef.current.then(task, task);
    writeQueueRef.current = next.catch(() => undefined);
    return next;
  }, []);

  // After each queued write, re-seed `base` from server-truth. The reconcile
  // is seed-source-aware (injected by the wrapper): the promised wrapper runs
  // `router.refresh()` (re-runs the layout's cookie read); the live wrapper
  // re-fetches `/api/cart` (on the static `sites` tree `router.refresh()`
  // re-seeds nothing — the cart isn't server-rendered there). `revalidateTag`
  // alone can't do this: the layout reads cookies directly, so no cache entry
  // re-runs and `base` would never pick up the fresh cookie.
  // Writes still queue one-at-a-time, but the re-seed is COALESCED: only the
  // write that drains the queue reconciles. Previously every write called
  // `onReconcile()`, and on the promised-seed tree that is `router.refresh()` —
  // a full RSC re-render of the whole cart route. Holding "+" five times fired
  // five of them, which is what made the traveller stepper feel janky. One
  // burst now costs one refresh.
  const pendingWritesRef = useRef(0);
  // Was `toast.error(...)`. A toast is the wrong instrument on a customer-
  // facing page: it appears in a corner away from the thing that failed, and
  // it takes itself away after a few seconds — a traveler who looked at their
  // phone misses that the seat count did not change and has no way to get the
  // message back. The cart page states this in place instead.
  const [writeError, setWriteError] = useState<string | null>(null);
  const dismissWriteError = useCallback(() => setWriteError(null), []);
  const runWrite = useCallback(
    (task: () => Promise<unknown>, errorMsg: string) => {
      // Incremented at CALL time (not inside the queued task) so a burst is
      // counted as in-flight before any of it starts running.
      pendingWritesRef.current += 1;
      // NOT cleared at call time. That belongs to `upsertLine` alone — see the
      // note there. Doing it here cost more than it bought on the cart page:
      // the banner is rendered on `writeError`, so clearing it the instant any
      // write STARTED unmounted the banner and remounting it on failure re-ran
      // its auto-scroll, snatching scroll and focus away on every retry; and a
      // failure the traveler had not read yet was wiped by an unrelated line's
      // write before that write's own result was known.
      // Resolves to WHETHER the write landed. Every existing caller ignores it
      // (`void runWrite(...)`), so this is additive — but a caller that needs
      // to say something true about the result, as the removal announcement
      // does, cannot get that from the optimistic list: the optimistic render
      // commits, so the row leaves `items` before the server is even asked.
      return enqueue(async () => {
        try {
          await task();
          // A write that works clears the last one that did not. Without this
          // the flag outlived the failure: `AddToCartDialog` early-returns on
          // it, so a blip on the cart page made every LATER, successful
          // add-to-cart report "We couldn't add this to your cart" over an
          // item that was in fact added. The contract on `writeError` already
          // promised this line existed.
          setWriteError(null);
          return true;
        } catch (err) {
          console.error(`[cart] ${errorMsg}`, err);
          // `errorMsg` names the ATTEMPT ("Could not remove this item"); it is
          // not the reason. Until this read the thrown error, the reason went
          // to the console and the traveler got the label whatever had
          // actually gone wrong.
          setWriteError(describeThrown(err, errorMsg));
          return false;
        } finally {
          pendingWritesRef.current -= 1;
          // Reconcile on drain even if the last write failed — that re-seed is
          // what pulls the UI back to server truth after a rejected change.
          if (pendingWritesRef.current === 0) onReconcile();
        }
      });
    },
    [onReconcile, enqueue],
  );

  const upsertLine = useCallback(
    (draft: CartLineDraft): string => {
      // Client mints the line ID so the optimistic dispatch and the
      // eventual server write share the same identity (no remount on
      // Server Action return).
      const id = newClientId("cart");
      const line: CartLine = {
        ...draft,
        id,
        addedAt: new Date().toISOString(),
        status: "valid",
        reservedUntil: nextHoldExpiry(),
      };
      startTransition(() => {
        dispatch({ type: "upsert", line });
      });
      // Clear at CALL time, and only on this path. `AddToCartDialog` opens
      // synchronously the moment this returns, while the write below is still
      // in flight — so a failure left over from an earlier, unrelated write
      // made a brand-new add-to-cart open on "Nothing was added" and then flip
      // to the success panel a few hundred milliseconds later. Clearing on
      // success alone (abb16379b) turned a stuck flag into that flash.
      //
      // The cart page's own writes deliberately do NOT clear here: nothing
      // reads the flag synchronously there, and clearing early only threw away
      // a message the traveler had not read.
      setWriteError(null);
      void runWrite(
        () => upsertLineAction(draft),
        "Could not save your cart change",
      );
      return id;
    },
    [dispatch, runWrite, setWriteError],
  );

  const updateLine = useCallback(
    (id: string, patch: Partial<CartLine>) => {
      startTransition(() => {
        dispatch({ type: "update", id, patch });
      });
      void runWrite(
        () => updateLineAction(id, patch),
        "Could not save your cart change",
      );
    },
    [dispatch, runWrite],
  );

  /** Resolves true once the server has actually taken the line, false if the
   *  write was rejected and `onReconcile` is about to put the row back. */
  const removeLine = useCallback(
    (id: string) => {
      startTransition(() => {
        dispatch({ type: "remove", id });
      });
      return runWrite(() => removeLineAction(id), "Could not remove this item");
    },
    [dispatch, runWrite],
  );

  const clearCart = useCallback(() => {
    startTransition(() => {
      dispatch({ type: "clear" });
    });
    void runWrite(() => clearCartAction(), "Could not clear your cart");
  }, [dispatch, runWrite]);

  const setStatus = useCallback(
    (id: string, status: CartLineStatus, message?: string) => {
      startTransition(() => {
        dispatch({ type: "status", id, status, message });
      });
      // Background validator — silent on failure (don't toast every
      // failed re-quote, the snapshot is still usable). Still queued so it
      // can't race with a concurrent upsert/update.
      void enqueue(() =>
        setLineStatusAction(id, status, message).catch(() => undefined),
      );
    },
    [dispatch, enqueue],
  );

  const setImageUrl = useCallback(
    (id: string, url: string) => {
      startTransition(() => {
        dispatch({ type: "image", id, url });
      });
      // Background URL refresh — silent on failure. Queued for the same
      // reason as setStatus.
      void enqueue(() => setLineImageAction(id, url).catch(() => undefined));
    },
    [dispatch, enqueue],
  );

  const value = useMemo<CartContextValue>(
    () => ({
      cart: optimisticCart,
      upsertLine,
      updateLine,
      removeLine,
      clearCart,
      setStatus,
      setImageUrl,
      writeError,
      dismissWriteError,
    }),
    [
      optimisticCart,
      upsertLine,
      updateLine,
      removeLine,
      clearCart,
      setStatus,
      setImageUrl,
      writeError,
      dismissWriteError,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

/** Client seed for the live wrapper — mirrors the `/api/cart` BFF envelope
 *  (`{ success, data }`) and `readCart`'s empty-on-missing contract: any
 *  non-ok response, `success:false`, missing body, or a rejected/aborted
 *  fetch resolves to `null` so the caller keeps its current base. */
async function fetchCart(signal?: AbortSignal): Promise<Cart | null> {
  try {
    // Trailing slash avoids the `trailingSlash: true` 308 round-trip.
    const res = await fetch("/api/cart/", { cache: "no-store", signal });
    if (!res.ok) return null;
    const body = (await res.json()) as { success?: boolean; data?: Cart };
    if (!body.success || !body.data) return null;
    return body.data;
  } catch {
    return null;
  }
}

/**
 * CartProvider — the **promised** seed wrapper (unchanged public signature).
 * The parent Server Component starts the signed-cookie read in parallel with
 * the render and passes the resulting `cartPromise`; `use()` unwraps it on
 * the client without blocking the page shell. Reconciles after a write via
 * `router.refresh()`, which re-runs the layout's cookie read. Used by
 * `(public)`/root — behaviour is identical to before the core extraction.
 */
export function CartProvider({
  children,
  cartPromise,
}: {
  children: ReactNode;
  cartPromise: Promise<Cart>;
}) {
  const base = use(cartPromise) ?? EMPTY_CART;
  const router = useRouter();
  const onReconcile = useCallback(() => router.refresh(), [router]);
  return (
    <CartProviderCore base={base} onReconcile={onReconcile}>
      {children}
    </CartProviderCore>
  );
}

/**
 * CartProviderLive — the **client-seed** wrapper for the static `sites`
 * tree, where the layout can't read the cart cookie server-side. Seeds the
 * core from a client `fetch('/api/cart')` on mount (the last known cart, or
 * EMPTY_CART, until it resolves) and reconciles after each write by
 * re-fetching `/api/cart` — `router.refresh()` can't re-seed here because
 * the cart isn't server-rendered on this tree. `useOptimistic` re-bases
 * whenever the fetched base changes, so a write's optimistic overlay settles
 * onto server-truth.
 */
export function CartProviderLive({ children }: { children: ReactNode }) {
  const [fetched, setFetched] = useState<Cart | null>(() => lastKnownCart);
  const base = fetched ?? EMPTY_CART;

  const onReconcile = useCallback(() => {
    void fetchCart().then((cart) => {
      if (cart) setFetched(cart);
    });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void fetchCart(controller.signal).then((cart) => {
      if (controller.signal.aborted) return;
      if (cart) setFetched(cart);
    });
    return () => controller.abort();
  }, []);

  return (
    <CartProviderCore base={base} onReconcile={onReconcile}>
      {children}
    </CartProviderCore>
  );
}

function useCartContext(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart hooks must be used inside <CartProvider>");
  }
  return ctx;
}

// ─── Public hook surface — same names + signatures as before ────────────

export function useCartLines(): CartLine[] {
  return useCartContext().cart.lines;
}

/** LINE count, which is what the cart's badge and labels show — deliberately
 *  not the number of distinct experiences. Lines dedup on
 *  (experience, package, date, time), so one tour can hold several; a count of
 *  distinct tours would disagree with the cards the traveller can see. See the
 *  note in `(checkout)/checkout/page.tsx` beside the "N tours" label. */
export function useCartCount(): number {
  return useCartContext().cart.lines.length;
}

export function useCartLine(
  id: string | null | undefined,
): CartLine | undefined {
  const { cart } = useCartContext();
  return id ? cart.lines.find((line) => line.id === id) : undefined;
}

export interface CartActions {
  upsertLine: (draft: CartLineDraft) => string;
  updateLine: (id: string, patch: Partial<CartLine>) => void;
  removeLine: (id: string) => Promise<boolean>;
  clearCart: () => void;
  setStatus: (id: string, status: CartLineStatus, message?: string) => void;
  setImageUrl: (id: string, url: string) => void;
}

/** The last cart write that failed, plus a way to dismiss it. Separate from
 *  `useCartActions` so a component that only writes does not re-render when the
 *  message changes. */
export function useCartWriteError(): {
  writeError: string | null;
  dismissWriteError: () => void;
} {
  const ctx = useCartContext();
  return {
    writeError: ctx.writeError,
    dismissWriteError: ctx.dismissWriteError,
  };
}

export function useCartActions(): CartActions {
  const ctx = useCartContext();
  return useMemo(
    () => ({
      upsertLine: ctx.upsertLine,
      updateLine: ctx.updateLine,
      removeLine: ctx.removeLine,
      clearCart: ctx.clearCart,
      setStatus: ctx.setStatus,
      setImageUrl: ctx.setImageUrl,
    }),
    [
      ctx.upsertLine,
      ctx.updateLine,
      ctx.removeLine,
      ctx.clearCart,
      ctx.setStatus,
      ctx.setImageUrl,
    ],
  );
}
